import React from 'react';
import { useConfigInstitucional } from 'hooks/useConfigInstitucional';
import { useAuth } from 'contexts/AuthContext';
import { CONFIG_GROUPS, CONFIG_META, ConfigClave, ConfiguracionHistorial } from 'types/configuracion.types';
import { configuracionService } from 'services/configuracionService';
import { catalogStorage } from 'utils/catalogStorage';

// ============================================================
// ConfigInstitucionPage — Configuración Institucional (BD)
// Acceso: ADMIN
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const SpinnerSmall = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const D_HISTORY = 'M10 2a8 8 0 1 0 0 16A8 8 0 0 0 10 2Zm.75 4.75a.75.75 0 0 0-1.5 0v3.5c0 .199.079.390.22.530l2.5 2.5a.75.75 0 1 0 1.06-1.06L10.75 9.69V6.75Z';

interface PendingConfirm {
  clave:        ConfigClave;
  label:        string;
  tipo:         string;
  valorActual:  string;
  valorNuevo:   string;
}

// ── Panel de historial de cambios ─────────────────────────────

const HistorialPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [historial, setHistorial]   = React.useState<ConfiguracionHistorial[]>([]);
  const [loading, setLoading]       = React.useState(true);
  const [error, setError]           = React.useState<string | null>(null);

  React.useEffect(() => {
    setLoading(true);
    configuracionService.getHistorial()
      .then(setHistorial)
      .catch(e => setError(e instanceof Error ? e.message : 'Error al cargar historial'))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('es-PE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-700 to-slate-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-white font-bold">Historial de cambios</h3>
            <p className="text-slate-300 text-xs mt-0.5">Auditoría de modificaciones de configuración</p>
          </div>
          <button onClick={onClose}
            className="text-slate-300 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex justify-center p-12"><SpinnerIcon /></div>
          )}
          {error && (
            <div className="m-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">{error}</div>
          )}
          {!loading && !error && historial.length === 0 && (
            <div className="flex flex-col items-center justify-center p-12 text-slate-400">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-12 h-12 mb-3 opacity-30">
                <path d={D_HISTORY}/>
              </svg>
              <p className="text-sm">No hay cambios registrados aún</p>
            </div>
          )}
          {!loading && historial.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Clave</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Anterior</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Nuevo</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Usuario</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {historial.map(h => (
                  <tr key={h.idHistorico} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{fmt(h.fechaModificacion)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-blue-700 whitespace-nowrap">{h.clave}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full font-mono">
                        {h.valorAnterior ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-mono">
                        {h.valorNuevo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{h.modificadoPor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Página principal ──────────────────────────────────────────

const ConfigInstitucionPage: React.FC = () => {
  const { user } = useAuth();
  void user;
  const { loading, error, saving, saveError, savedKey, actualizar, getValor } = useConfigInstitucional();
  const [localValues,    setLocalValues]    = React.useState<Record<string, string>>({});
  const [pendingConfirm, setPendingConfirm] = React.useState<PendingConfirm | null>(null);
  const [showHistorial,  setShowHistorial]  = React.useState(false);

  React.useEffect(() => {
    if (!loading) {
      const initial: Record<string, string> = {};
      CONFIG_GROUPS.forEach(g => g.claves.forEach(c => { initial[c] = getValor(c); }));
      setLocalValues(initial);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const setLocal = (clave: string, valor: string) =>
    setLocalValues(prev => ({ ...prev, [clave]: valor }));

  const handleGuardarClick = (clave: ConfigClave) => {
    const meta = CONFIG_META[clave];
    setPendingConfirm({
      clave,
      label:       meta.label,
      tipo:        meta.tipo,
      valorActual: getValor(clave),
      valorNuevo:  localValues[clave] ?? '',
    });
  };

  const handleConfirmar = async () => {
    if (!pendingConfirm) return;
    await actualizar(pendingConfirm.clave, pendingConfirm.valorNuevo);
    // Sincronizar caché del sello inmediatamente tras guardar
    if (pendingConfirm.clave === 'IE_SELLO_BASE64') {
      if (pendingConfirm.valorNuevo) catalogStorage.saveSelloBase64(pendingConfirm.valorNuevo);
      else catalogStorage.clearSelloBase64();
    }
    setPendingConfirm(null);
  };

  const formatValor = (valor: string, tipo: string, clave?: ConfigClave) => {
    if (tipo === 'boolean') return valor === 'true' ? 'Activado' : 'Desactivado';
    if (tipo === 'select' && clave) {
      const meta = CONFIG_META[clave];
      const op = meta.options?.find(o => o.value === valor);
      return op ? op.label : (valor || '(vacío)');
    }
    return valor || '(vacío)';
  };

  // Aviso adicional para mostrar en el modal cuando el valor nuevo coincide
  // con alguna entrada de avisoConfirmar de la clave.
  const getAvisoConfirmar = (pc: PendingConfirm | null): string | null => {
    if (!pc) return null;
    const meta = CONFIG_META[pc.clave];
    return meta.avisoConfirmar?.find(a => a.cuandoValor === pc.valorNuevo)?.mensaje ?? null;
  };

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Configuración Institucional</h1>
            <p className="text-blue-200 mt-1">
              Parámetros del sistema almacenados en la base de datos. Los cambios se aplican de inmediato.
            </p>
          </div>
          <button
            onClick={() => setShowHistorial(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20
                       text-white text-sm font-semibold transition-colors flex-shrink-0">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d={D_HISTORY}/>
            </svg>
            Historial
          </button>
        </div>
      </div>

      {loading && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 flex justify-center">
          <SpinnerIcon />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">{error}</div>
      )}

      {!loading && CONFIG_GROUPS.map(group => (
        <div key={group.titulo} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-700">{group.titulo}</h3>
            <p className="text-slate-400 text-xs mt-0.5">{group.descripcion}</p>
          </div>
          <div className="p-6 space-y-4">
            {group.claves.map((clave: ConfigClave) => {
              const meta = CONFIG_META[clave];
              const isSaving = saving === clave;
              const isSaved = savedKey === clave;
              const localVal = localValues[clave] ?? '';

              return (
                <div key={clave} className="flex items-start gap-4">
                  <div className="flex-1">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">
                      {meta.label}
                    </label>
                    {meta.tipo === 'boolean' ? (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setLocal(clave, localVal === 'true' ? 'false' : 'true')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                            ${localVal === 'true' ? 'bg-blue-600' : 'bg-slate-300'}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
                            ${localVal === 'true' ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <span className="text-sm text-slate-700">
                          {localVal === 'true' ? 'Activado' : 'Desactivado'}
                        </span>
                      </div>
                    ) : meta.tipo === 'imagen' ? (
                      <div className="flex items-start gap-4">
                        {/* Vista previa del sello actual */}
                        <div className="w-24 h-24 border-2 border-dashed border-slate-200 rounded-xl
                                        flex items-center justify-center bg-slate-50 flex-shrink-0 overflow-hidden">
                          {localVal ? (
                            <img
                              src={localVal}
                              alt="Sello IE"
                              className="w-full h-full object-contain p-1"
                            />
                          ) : (
                            <span className="text-xs text-slate-300 text-center px-2">Sin sello</span>
                          )}
                        </div>
                        <div className="flex-1 space-y-2">
                          <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200
                                            bg-white hover:bg-slate-50 cursor-pointer transition-colors text-sm
                                            font-semibold text-slate-600 w-fit">
                            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-blue-600">
                              <path fillRule="evenodd" d="M1 5.25A2.25 2.25 0 0 1 3.25 3h13.5A2.25 2.25 0 0 1 19 5.25v9.5A2.25 2.25 0 0 1 16.75 17H3.25A2.25 2.25 0 0 1 1 14.75v-9.5Zm1.5 5.81v3.69c0 .414.336.75.75.75h13.5a.75.75 0 0 0 .75-.75v-2.69l-2.22-2.219a.75.75 0 0 0-1.06 0l-1.91 1.909-.48-.480a.75.75 0 0 0-1.06 0L6.53 11.06l-2.28-2.281a.75.75 0 0 0-1.06 0L1.5 11.061Zm7.75-7.56a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z" clipRule="evenodd"/>
                            </svg>
                            Seleccionar imagen
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/jpg"
                              className="hidden"
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const reader = new FileReader();
                                reader.onload = ev => setLocal(clave, ev.target?.result as string);
                                reader.readAsDataURL(file);
                              }}
                            />
                          </label>
                          {localVal && (
                            <button
                              onClick={() => setLocal(clave, '')}
                              className="text-xs text-red-500 hover:text-red-700 transition-colors">
                              Eliminar sello
                            </button>
                          )}
                        </div>
                      </div>
                    ) : meta.tipo === 'time' ? (
                      <input
                        type="time"
                        value={localVal ? localVal.slice(0, 5) : ''}
                        onChange={e => setLocal(clave, e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800
                                   outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      />
                    ) : meta.tipo === 'select' ? (
                      <div className="space-y-2">
                        <select
                          value={localVal}
                          onChange={e => setLocal(clave, e.target.value)}
                          className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800
                                     bg-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        >
                          {(meta.options ?? []).map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        {/* Tarjetas de descripción por opción */}
                        {(meta.options ?? []).some(o => o.description) && (
                          <div className="grid sm:grid-cols-2 gap-2 mt-2">
                            {(meta.options ?? []).map(o => (
                              <div
                                key={o.value}
                                className={`text-xs rounded-lg border p-2.5 transition-colors
                                  ${localVal === o.value
                                    ? 'bg-blue-50 border-blue-200 text-slate-700'
                                    : 'bg-slate-50 border-slate-100 text-slate-500'}`}
                              >
                                <p className="font-bold uppercase tracking-wide mb-0.5">{o.label}</p>
                                {o.description && <p className="leading-snug">{o.description}</p>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <input
                        type={meta.tipo === 'decimal' || meta.tipo === 'entero' ? 'number' : 'text'}
                        step={meta.tipo === 'decimal' ? '0.1' : '1'}
                        min={meta.min}
                        value={localVal}
                        onChange={e => setLocal(clave, e.target.value)}
                        placeholder={meta.hint}
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800
                                   outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      />
                    )}
                    {meta.hint && meta.tipo !== 'boolean' && meta.tipo !== 'imagen' && meta.tipo !== 'select' && (
                      <p className={`text-xs mt-1 ${
                        meta.hintLevel === 'warning'
                          ? 'text-amber-700 font-semibold'
                          : 'text-slate-400'
                      }`}>
                        {meta.hint}
                      </p>
                    )}
                    {meta.hint && meta.tipo === 'select' && (
                      <p className="text-xs mt-1 text-slate-400">{meta.hint}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 pt-5 flex-shrink-0">
                    {isSaved && (
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-green-600">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
                      </svg>
                    )}
                    <button
                      onClick={() => handleGuardarClick(clave)}
                      disabled={isSaving}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold
                                 bg-blue-700 hover:bg-blue-800 text-white transition-colors shadow-sm disabled:opacity-60">
                      {isSaving ? <SpinnerSmall /> : 'Guardar'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">{saveError}</div>
      )}

      {/* Modal de confirmación */}
      {pendingConfirm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4">
              <h3 className="text-white font-bold">Confirmar cambio</h3>
              <p className="text-amber-100 text-xs mt-0.5">Esta acción modifica un parámetro del sistema</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Parámetro</p>
                <p className="text-sm font-semibold text-slate-800">{pendingConfirm.label}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl px-4 py-3 text-center">
                  <p className="text-xs font-bold text-slate-400 uppercase mb-1">Valor actual</p>
                  <p className="text-sm font-semibold text-slate-600">
                    {formatValor(pendingConfirm.valorActual, pendingConfirm.tipo, pendingConfirm.clave)}
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-center">
                  <p className="text-xs font-bold text-blue-400 uppercase mb-1">Nuevo valor</p>
                  <p className="text-sm font-bold text-blue-700">
                    {formatValor(pendingConfirm.valorNuevo, pendingConfirm.tipo, pendingConfirm.clave)}
                  </p>
                </div>
              </div>

              {/* Aviso especial cuando el cambio amerita advertencia */}
              {(() => {
                const aviso = getAvisoConfirmar(pendingConfirm);
                if (!aviso) return null;
                return (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5">
                      <path fillRule="evenodd" d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495ZM10 6a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 6Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
                    </svg>
                    <p className="text-xs text-amber-800 leading-snug">{aviso}</p>
                  </div>
                );
              })()}
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between">
              <button
                onClick={() => setPendingConfirm(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmar}
                disabled={saving === pendingConfirm.clave}
                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold transition-colors shadow-sm disabled:opacity-60"
              >
                {saving === pendingConfirm.clave && <SpinnerSmall />}
                Confirmar cambio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Panel de historial */}
      {showHistorial && <HistorialPanel onClose={() => setShowHistorial(false)} />}
    </div>
  );
};

export default ConfigInstitucionPage;
